import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { cx } from "@jugl-web/utils";
import {
  ComponentType,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  SingletonPopoverContext,
  SingletonPopoverContextValue,
} from "./SingletonPopoverContext";
import { SingletonPopoverState } from "./types";

interface SingletonPopoverProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages a single reusable popover instance across wrapped components.
 * This is a performance optimization for heavy interactive data (e.g., tables with many rows),
 * as it avoids creating multiple popover instances and instead reuses a single one.
 */
export const SingletonPopoverProvider: FC<SingletonPopoverProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<SingletonPopoverState | null>(null);

  const handleOpenChange = useCallback((open: boolean, event?: Event) => {
    if (open) {
      setIsOpen(open);
      return;
    }

    // Prevent closing the popover when interacting with HeadlessUI components
    // (supports nested popovers, menus, etc.)
    if (event?.type === "focusout" || event?.type === "blur") {
      const focusEvent = event as FocusEvent;
      const target = focusEvent.relatedTarget as HTMLElement | null;

      if (
        target?.closest("[data-headlessui-portal]") ||
        target?.id?.includes("headlessui")
      ) {
        return;
      }
    }

    setIsOpen(false);
  }, []);

  const { refs, floatingStyles, context, elements, isPositioned } = useFloating(
    {
      open: isOpen,
      onOpenChange: handleOpenChange,
      placement: "bottom",
      middleware: [
        flip({
          fallbackStrategy: "bestFit",
          fallbackAxisSideDirection: "start",
        }),
        shift(),
        offset({ mainAxis: 4 }),
      ],
      whileElementsMounted: autoUpdate,
    }
  );

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: {
      opacity: 0,
      transform: "scale(0.95)",
    },
    open: {
      opacity: 1,
      transform: "scale(1)",
    },
    close: {
      opacity: 0,
      transform: "scale(0.95)",
    },
  });

  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      const target = event.target as HTMLElement;

      if (
        target.closest("[data-headlessui-portal]") ||
        target?.id?.includes("headlessui")
      ) {
        return false;
      }

      return true;
    },
  });
  const role = useRole(context, { role: "dialog" });
  const focus = useFocus(context);

  const { getFloatingProps } = useInteractions([dismiss, role, focus]);

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openPopover = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    <TComponent extends ComponentType<any>>(
      incomingState: SingletonPopoverState<TComponent>
    ) => {
      setState(incomingState as SingletonPopoverState);
      setIsOpen(true);
      refs.setReference(incomingState.triggerElement);
    },
    [refs]
  );

  const isPopoverOpen = useCallback(
    (popoverId: string) => state?.id === popoverId && isOpen,
    [isOpen, state?.id]
  );

  const contextValue = useMemo<SingletonPopoverContextValue>(
    () => ({
      openPopover,
      closePopover,
      isPopoverOpen,
      _hasContext: true,
    }),
    [closePopover, isPopoverOpen, openPopover]
  );

  return (
    <SingletonPopoverContext.Provider value={contextValue}>
      {children}
      {state && isMounted && (
        <FloatingPortal>
          <FloatingOverlay className="z-[99] bg-transparent">
            <FloatingFocusManager context={context} modal={false}>
              {/* Outer wrapper - positioning only */}
              <div
                ref={refs.setFloating}
                className="z-[100]"
                style={{
                  ...floatingStyles,
                  // If it's not positioned, or the reference is gone from DOM, hide it
                  visibility:
                    isPositioned && elements.domReference?.isConnected
                      ? "visible"
                      : "hidden",
                }}
                {...getFloatingProps()}
              >
                {/* Inner wrapper - animation and appearance */}
                <div
                  className={cx(
                    "rounded-lg bg-white shadow-[0px_8px_16px_rgba(0,0,0,0.12)]",
                    state.popoverClassName
                  )}
                  style={transitionStyles}
                >
                  {state.renderContent ? (
                    state.renderContent()
                  ) : state.Component ? (
                    <state.Component {...state.componentProps} />
                  ) : null}
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </SingletonPopoverContext.Provider>
  );
};
